import React, { useEffect, useState } from "react";
import { Plus, Pencil, X, Target } from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  ON_TRACK: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  AT_RISK:  "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  BEHIND:   "text-red-400 bg-red-500/10 border-red-500/20",
  DONE:     "text-white/30 bg-white/5 border-white/10",
};

const EMPTY = { title: "", description: "", owner: "", due_date: "", progress: 0, status: "ON_TRACK", type: "ORG" };

export default function GoalsTab() {
  const [goals,   setGoals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft,   setDraft]   = useState(EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/performance/org-goals`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setGoals(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(null);
    const isNew = editing === "new";
    try {
      const res = await fetch(
        isNew ? `${API}/api/admin/performance/org-goals` : `${API}/api/admin/performance/org-goals/${draft.id}`,
        {
          method: isNew ? "POST" : "PATCH",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(draft),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load(); setEditing(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const del = (id) => confirmDelete("this org goal", async () => {
    try {
      const res = await fetch(`${API}/api/admin/performance/org-goals/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setGoals(g => g.filter(x => x.id !== id));
      toast.success("Goal deleted");
    } catch (e) {
      setError(e.message);
      toast.error("Failed to delete goal");
    }
  });

  const updateProgress = async (id, progress) => {
    try {
      const res = await fetch(`${API}/api/admin/performance/org-goals/${id}`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ progress }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setGoals(g => g.map(x => x.id === id ? { ...x, progress } : x));
      toast.success("Progress updated");
    } catch (err) {
      console.error("Failed to update goal progress:", err);
      setError("Failed to save. Try again.");
    }
  };

  const byType = goals.reduce((acc, g) => {
    const t = g.type || "ORG";
    if (!acc[t]) acc[t] = [];
    acc[t].push(g);
    return acc;
  }, {});

  return (
    <div className="space-y-5">

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Edit form */}
      {editing && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing === "new" ? "New Goal" : `Edit — ${draft.title}`}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={labelCls}>Goal Title</label>
              <input className={inputCls} value={draft.title} onChange={e => setDraft(d => ({...d, title: e.target.value}))} placeholder="e.g. Reduce attrition to <8%" /></div>
            <div><label className={labelCls}>Owner</label>
              <input className={inputCls} value={draft.owner || ""} onChange={e => setDraft(d => ({...d, owner: e.target.value}))} placeholder="e.g. CHRO" /></div>
            <div><label className={labelCls}>Type</label>
              <select className={inputCls} value={draft.type || "ORG"} onChange={e => setDraft(d => ({...d, type: e.target.value}))}>
                {["ORG","DEPT","TEAM"].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Due Date</label>
              <input type="date" className={inputCls} value={draft.due_date || ""} onChange={e => setDraft(d => ({...d, due_date: e.target.value}))} /></div>
            <div><label className={labelCls}>Status</label>
              <select className={inputCls} value={draft.status || "ON_TRACK"} onChange={e => setDraft(d => ({...d, status: e.target.value}))}>
                {["ON_TRACK","AT_RISK","BEHIND","DONE"].map(s => <option key={s}>{s}</option>)}
              </select></div>
            <div><label className={labelCls}>Progress %</label>
              <input type="number" min="0" max="100" className={inputCls} value={draft.progress || 0} onChange={e => setDraft(d => ({...d, progress: Number(e.target.value)}))} /></div>
            <div className="col-span-2"><label className={labelCls}>Description</label>
              <input className={inputCls} value={draft.description || ""} onChange={e => setDraft(d => ({...d, description: e.target.value}))} /></div>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => setEditing(null)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.title}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save Goal"}
            </button>
          </div>
        </div>
      )}

      {/* Goals by type */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target size={13} className="text-[#00ff90]/60" />
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Organisational Goals</span>
          </div>
          <button onClick={() => { setDraft(EMPTY); setEditing("new"); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> Add Goal
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="h-16 rounded-lg bg-white/5 animate-pulse" />)
          : goals.length === 0 ? <p className="text-white/20 text-sm italic text-center py-4">No org goals set.</p>
          : Object.entries(byType).map(([type, typeGoals]) => (
            <div key={type}>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-3">{type} Goals</p>
              <div className="space-y-3">
                {typeGoals.map(g => {
                  const statusStyle = STATUS_STYLE[g.status] || STATUS_STYLE.ON_TRACK;
                  return (
                    <div key={g.id} className="rounded-xl border border-white/5 bg-white/3 p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="text-sm text-white/80 font-medium">{g.title}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>
                              {(g.status || "").replace("_"," ")}
                            </span>
                          </div>
                          {g.owner    && <p className="text-xs text-white/30">Owner: {g.owner}</p>}
                          {g.due_date && <p className="text-xs text-white/20">Due: {g.due_date}</p>}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => { setDraft({...g}); setEditing(g.id); }}
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => del(g.id)}
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                            <X size={12} />
                          </button>
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-[#00ff90] transition-all" style={{ width: `${g.progress || 0}%` }} />
                        </div>
                        <span className="text-xs font-medium text-[#00ff90] w-8 text-right">{g.progress || 0}%</span>
                        <input type="range" min="0" max="100" value={g.progress || 0}
                          onChange={e => updateProgress(g.id, Number(e.target.value))}
                          className="w-24 accent-[#00ff90]" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}